import { refreshHeadline } from "./format";
import type { FrsRefreshStatus, FrsRefreshView } from "./types";

export const ACTIVE_POLL_MS = 4000;
export const IDLE_POLL_MS = 30000;
export const STALLED_AFTER_SECONDS = 300;

export type StepState = "done" | "current" | "todo";

export interface StepSegment {
  state: StepState;
  fill: number;
}

export type Tone = "ok" | "info" | "bad";

export interface FinishedSummary {
  tone: Tone;
  title: string;
  text: string;
}

export function isActive(status: FrsRefreshStatus | null): boolean {
  return status !== null && status.state !== "Idle";
}

export function pollDelayMs(status: FrsRefreshStatus | null): number {
  return isActive(status) ? ACTIVE_POLL_MS : IDLE_POLL_MS;
}

export function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, "0")}s`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

export function startedBy(status: FrsRefreshStatus): string | null {
  if (status.triggeredByName) return `Started by ${status.triggeredByName}`;
  if (status.triggeredBy === "Schedule") return "Started by the daily check";
  if (status.triggeredBy === "Admin") return "Started by an admin";
  return null;
}

export function stalledMessage(status: FrsRefreshStatus): string | null {
  const silent = status.secondsSinceUpdate;
  if (status.reportsProgress !== true || silent === null || silent < STALLED_AFTER_SECONDS) return null;
  const minutes = Math.floor(silent / 60);
  return `The worker has not reported for ${minutes} minutes and may have stopped.`;
}

export const QUIET_AFTER_SECONDS = 90;

export function workerLabel(status: FrsRefreshStatus): string | null {
  return status.workerHost ? `On ${status.workerHost}` : null;
}

export function noProgressNote(status: FrsRefreshStatus, elapsedSeconds: number | null): string | null {
  if (status.state !== "Running" || status.stage || elapsedSeconds === null) return null;
  if (elapsedSeconds < QUIET_AFTER_SECONDS) return null;
  const host = status.workerHost ? ` (${status.workerHost})` : "";
  if (status.reportsProgress === false) {
    return `This worker${host} does not report progress, so the steps cannot be shown. It may be running an older version, and the refresh is probably still going.`;
  }
  return `A worker${host} took this job but has not started it. If this lasts, it may be running an older version that does not know this job.`;
}

export function activeHeadline(status: FrsRefreshStatus): string {
  if (status.state === "Queued") return "Refresh queued";
  if (status.stage && status.stageIndex !== null && status.stageTotal !== null) {
    return `Refreshing from EPA: step ${status.stageIndex} of ${status.stageTotal}`;
  }
  return status.reportsProgress === false ? "Refreshing from EPA" : "Refresh starting";
}

export function stepSegments(
  index: number | null,
  total: number | null,
  percent: number | null,
): StepSegment[] {
  if (index === null || total === null || total <= 0) return [];
  return Array.from({ length: total }, (_, position) => {
    const step = position + 1;
    if (step < index) return { state: "done", fill: 1 };
    if (step === index) return { state: "current", fill: Math.max(0, Math.min(100, percent ?? 0)) / 100 };
    return { state: "todo", fill: 0 };
  });
}

export function justFinished(previous: FrsRefreshStatus | null, next: FrsRefreshStatus): boolean {
  return previous !== null && previous.state !== "Idle" && next.state === "Idle";
}

export function finishedSummary(run: FrsRefreshView): FinishedSummary {
  if (run.status === "Failed") {
    return { tone: "bad", title: "The last refresh failed", text: run.lastError ?? "No reason was recorded." };
  }
  if (run.status === "Skipped") {
    return {
      tone: "info",
      title: "Nothing new to load",
      text: "EPA's file has not changed since the last refresh. Tick the box to load it again anyway.",
    };
  }
  return { tone: "ok", title: "Refresh finished", text: refreshHeadline(run) };
}

export function problemSummary(status: FrsRefreshStatus | null): FinishedSummary | null {
  if (status === null || status.state !== "Idle" || !status.problem) return null;
  return { tone: "bad", title: "The refresh did not run", text: status.problem };
}

export function queuedDetail(status: FrsRefreshStatus): string {
  return `Waiting for a worker to pick it up. It is dropped after ${status.queueTimeoutMinutes} minutes if none does.`;
}
